<template>
  <v-row>
    <!-- LISTA LATERAL -->
    <v-col cols="12" md="4" class="pa-2">
      <div class="list-container">
        <!-- Campo de busca + botão de reset -->
        <div class="search-box d-flex align-center">
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Buscar localidade"
            dense
            hide-details
            class="flex-grow-1"
          ></v-text-field>

          <v-btn
            icon
            title="Limpar seleção"
            @click="resetSelection"
            :disabled="!selected"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>

        <!-- Lista com scroll -->
        <div class="list-scroll">
          <v-list dense>
            <v-list-item
              v-for="(loc, index) in filteredList"
              :key="loc.id"
              @click="focusMarker(loc)"
              :ref="'listItem-' + index"
              :class="{
                'grey lighten-3': selected?.id === loc.id,
                pointer: true
              }"
            >
              <v-list-item-content>
                <v-list-item-title>{{ loc.city }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ loc.state }} - {{ loc.zipcode }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </div>
    </v-col>

    <!-- MAPA -->
    <v-col cols="12" md="8" class="pa-2">
      <div ref="map" class="gmap"></div>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'GMapPinsList',

  props: {
    points: { type: Array, default: () => [] },
    apiKey: { type: String, required: true },
  },

  data() {
    return {
      map: null,
      markers: [],
      bounds: null,
      search: '',
      selected: null,
      defaultIcon: null,
      activeIcon: null,
    };
  },

  computed: {
    /** Converte os pontos e ordena alfabeticamente */
    locationList() {
      return this.points
        .filter((p) => p.type === 'Point' && p.original?.region?.city)
        .map((p, i) => ({
          id: i,
          city: p.original.region.city.long_name || 'Desconhecida',
          state: p.original.region.state?.short_name || '',
          zipcode: p.original.zipcode || '',
          position: {
            lat: p.coordinates[1],
            lng: p.coordinates[0],
          },
        }))
        .sort((a, b) => a.city.localeCompare(b.city));
    },

    filteredList() {
      const s = this.search.toLowerCase();
      return this.locationList.filter((l) =>
        `${l.city} ${l.state} ${l.zipcode}`.toLowerCase().includes(s)
      );
    },
  },

  watch: {
    points: {
      handler() {
        if (this.map) {
          this.clearMarkers();
          this.addMarkers();
          this.fitBoundsToAllMarkers();
        }
      },
      deep: true,
    },
  },

  mounted() {
    if (!window.google || !window.google.maps) {
      this.loadGoogleMapsScript()
        .then(() => this.initMap())
        .catch((err) => console.error('Erro ao carregar Google Maps:', err));
    } else {
      this.initMap();
    }
  },

  methods: {
    loadGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        if (document.getElementById('google-maps-script')) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    initMap() {
      this.map = new window.google.maps.Map(this.$refs.map, {
        center: { lat: -14.235, lng: -51.9253 },
        zoom: 4,
        mapTypeControl: false,
      });

      // Define ícones padrão e ativo
      this.defaultIcon = {
        url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
      };
      this.activeIcon = {
        url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
      };

      this.addMarkers();
      this.fitBoundsToAllMarkers();
    },

    addMarkers() {
      this.bounds = new window.google.maps.LatLngBounds();

      this.filteredList.forEach((loc) => {
        const marker = new window.google.maps.Marker({
          position: loc.position,
          map: this.map,
          title: loc.city,
          icon: this.defaultIcon,
        });

        marker.addListener('click', () => {
          this.selectMarker(loc.id);
        });

        this.markers.push({ id: loc.id, marker });
        this.bounds.extend(marker.getPosition());
      });
    },

    clearMarkers() {
      this.markers.forEach(({ marker }) => marker.setMap(null));
      this.markers = [];
    },

    fitBoundsToAllMarkers() {
      if (!this.bounds || this.markers.length === 0) return;
      this.map.fitBounds(this.bounds);

      const listener = window.google.maps.event.addListener(
        this.map,
        'bounds_changed',
        () => {
          const currentZoom = this.map.getZoom();
          if (currentZoom > 13) this.map.setZoom(13);
          window.google.maps.event.removeListener(listener);
        }
      );
    },

    /** Seleciona um marcador e sincroniza com a lista */
    selectMarker(id) {
      const loc = this.locationList.find((l) => l.id === id);
      if (!loc) return;

      // Atualiza ícones
      this.markers.forEach(({ id: mid, marker }) => {
        marker.setIcon(mid === id ? this.activeIcon : this.defaultIcon);
        marker.setAnimation(mid === id ? window.google.maps.Animation.DROP : null);
      });

      this.selected = loc;
      this.map.panTo(loc.position);
      this.map.setZoom(12);

      this.scrollToListItem(loc.id);
    },

    /** Clicar na lista */
    focusMarker(loc) {
      this.selectMarker(loc.id);
    },

    /** Rola a lista até o item correspondente */
    scrollToListItem(id) {
      this.$nextTick(() => {
        const index = this.filteredList.findIndex((l) => l.id === id);
        const el = this.$refs[`listItem-${index}`]?.[0];
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    /** Reseta seleção e volta à visão geral */
    resetSelection() {
      this.selected = null;
      this.markers.forEach(({ marker }) => marker.setIcon(this.defaultIcon));
      this.fitBoundsToAllMarkers();
    },
  },

  beforeDestroy() {
    this.clearMarkers();
    this.map = null;
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

.list-container {
  display: flex;
  flex-direction: column;
  height: 500px; /* mesma altura do mapa */
}

.search-box {
  flex: 0 0 auto;
  gap: 8px;
}

.list-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 4px;
}

.gmap {
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
}
</style>
